import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import dayjs from "dayjs"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import EditCertificationForm from "../forms/EditCertificationForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { IMAGE_MIME_TYPES_WITH_PDF } from "@/components/upload/constants"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import useHandleFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import type { File as AerosFile, CompanyCertification } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import useCompanyCertificationMutation from "@/screens/Companies/edit/components/certification/hooks/useCompanyCertificationMutation"
import { UpdateCompanyCertificationFormSchema } from "@/screens/Companies/edit/components/certification/schema/CompanyCertificationSchema"
import type { UpdateCompanyCertificationFormValues } from "@/screens/Companies/edit/components/certification/schema/CompanyCertificationSchema"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface EditCertificationDialogProps {
  certification?: CompanyCertification
  companyId: string
  onClose: () => void
  onSuccess: () => void
}

const EditCertificationDialog: FC<EditCertificationDialogProps> = ({
  companyId,
  certification,
  onClose,
  onSuccess,
}) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updateCertification } = useCompanyCertificationMutation()

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState<boolean>(false)

  const { fileList, fileFetching } = useGetFileUploadData({
    referenceId: certification?.companyCertificationId as string,
    fieldGroups: [FieldGroupEnum.CompanyCertification],
    referenceTableName: ReferenceTableName.Company,
  })

  const file = fileList?.[0]?.file as AerosFile

  const { uploadFile, onMarkFileAsNeedRemoval, removeFiles } = useHandleFileUpload({
    referenceTableName: ReferenceTableName.Company,
    allowedFileTypes: IMAGE_MIME_TYPES_WITH_PDF,
  })

  const methods = useForm<UpdateCompanyCertificationFormValues>({
    values: {
      certificationType: certification?.certificationType ?? "",
      locationId: certification?.locationId ?? "",
      companyCertifications: [
        {
          effectiveDate: certification?.effectiveDate ?? "",
          expirationDate: certification?.expirationDate ?? "",
          image: file ?? null,
        },
      ],
    },
    mode: "all",
    resolver: zodResolver(UpdateCompanyCertificationFormSchema),
  })

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
  } = methods

  const { fields: companyCertificationFields } = useFieldArray({
    control: methods.control,
    name: "companyCertifications",
  })

  const isUpcoming = dayjs(certification?.effectiveDate).isAfter(dayjs())
  const isSaveButtonDisabled = !isValid || !isDirty || isSubmitting

  const handleSubmitData = async ({ companyCertifications }: UpdateCompanyCertificationFormValues) => {
    const { image, expirationDate, effectiveDate } = companyCertifications[0]
    const { error, data: updatedCertification } = await updateCertification({
      input: {
        expirationDate,
        effectiveDate,
        companyCertificationId: certification?.companyCertificationId as string,
        companyId: companyId,
      },
    })
    const hasError =
      error?.message ||
      (updatedCertification?.companyCertification.update.__typename === "CompanyCertificationFailure" &&
        Boolean(updatedCertification?.companyCertification.update.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to update certification",
      })
      return
    }

    // Handle file uploads
    if (image && image instanceof File) {
      try {
        await uploadFile({
          file: image,
          referenceId: certification?.companyCertificationId as string,
          type: FieldGroupEnum.CompanyCertification,
        })
      } catch (error) {
        notify({
          message: error instanceof Error ? error.message : "Unable to update certification file. Please try again.",
          type: NotificationType.Error,
        })
      }
    }

    await removeFiles()

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Certification successfully updated",
    })
    onSuccess()
  }

  const handleConfirm = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleMarkFileAsNeedRemoval = () => {
    const fileIdToRemove = file?.fileId
    if (fileIdToRemove) {
      onMarkFileAsNeedRemoval(fileIdToRemove)
    }
  }
  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog
          title='Are you sure you want to cancel?'
          message='By cancelling all of the information inputed will be lost. Confirm by clicking on “Yes, Cancel” or “Go Back” to keep editing information.'
          onClose={() => setIsCancelConfirmationOpen(false)}
          onConfirm={onClose}
        />
      )}
      <Dialog open onClose={handleConfirm} fullWidth sx={{ display: isCancelConfirmationOpen ? "none" : "block" }}>
        <DialogHeader title={`Update ${isUpcoming ? "Upcoming" : "Current"} Certification`} onClose={handleConfirm} />
        <DialogContent classes={{ root: "p-10" }}>
          <FormProvider {...methods}>
            <EditCertificationForm
              fields={companyCertificationFields}
              companyId={companyId}
              file={file as File}
              onMarkFileAsNeedRemoval={handleMarkFileAsNeedRemoval}
              fileFetching={fileFetching}
            />
          </FormProvider>
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button appearance='outlined' onClick={handleConfirm} data-testid='edit-certification-cancel-button'>
            Cancel
          </Button>
          <Box className='flex gap-2'>
            <Button
              disabled={isSaveButtonDisabled}
              onClick={handleSubmit(handleSubmitData)}
              data-testid='edit-certification-save-button'
            >
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditCertificationDialog
